<template>
	<v-container
		fluid
		class="system-list-container"
	>
		<v-row justify="center">
			<v-col
				cols="12"
				md="12"
				lg="12"
				xl="8"
				class="system-list"
			>
				<!-- Search Filter -->
				<div class="search-filter-container mb-6">
					<div class="d-flex align-center gap-3">
						<v-text-field
							v-model="searchQuery"
							label="Search systems..."
							placeholder="Search by system or service name"
							prepend-inner-icon="mdi-magnify"
							clearable
							outlined
							dense
							hide-details
							class="search-input flex-grow-1"
							data-testid="system-search-input"
						/>
						<div class="sort-container">
							<span class="sort-label">
								Sort:
							</span>
							<v-btn-toggle
								v-model="sortMode"
								class="sort-toggle"
								mandatory
								borderless
								color="primary"
								dense
							>
								<v-tooltip bottom>
									<template #activator="{ on, attrs }">
										<v-btn
											value="newest"
											data-testid="sort-newest-button"
											class="sort-btn"
											x-small
											v-bind="attrs"
											v-on="on"
										>
											<v-icon size="14">
												mdi-clock-time-four-outline
											</v-icon>
										</v-btn>
									</template>
									<span>
										Sort by newest first
									</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template #activator="{ on, attrs }">
										<v-btn
											value="alphabetical"
											data-testid="sort-alphabetical-button"
											class="sort-btn"
											x-small
											v-bind="attrs"
											v-on="on"
										>
											<v-icon size="14">
												mdi-sort-alphabetical-variant
											</v-icon>
										</v-btn>
									</template>
									<span>
										Sort alphabetically (A-Z)
									</span>
								</v-tooltip>
							</v-btn-toggle>
						</div>
						<v-spacer />
						<v-btn
							v-if="userHasFullConnectorServicePermission"
							color="primary"
							depressed
							data-testid="add-system-button"
							class="primary-action-button text-none"
							:disabled="isLookupDataLoading"
							:loading="isLookupDataLoading"
							@click="handleCreateSystem"
						>
							<v-icon left>
								mdi-plus
							</v-icon>
							Add System
						</v-btn>
					</div>
				</div>

				<!-- System Cards List -->
				<div class="system-cards-list">
					<div
						v-if="filteredSystems.length === 0 && searchQuery"
						class="empty-search-state"
					>
						<!-- Empty Search State -->
						<v-card
							class="empty-state-card text-center pa-6"
							outlined
						>
							<v-icon
								size="40"
								color="grey lighten-2"
								class="mb-3"
							>
								mdi-file-search-outline
							</v-icon>
							<h4 class="mb-2 grey--text text--darken-1">
								No systems found
							</h4>
							<p class="mb-4 grey--text text--lighten-1 text-body-2">
								No systems match your search for "{{ searchQuery }}". Try adjusting your search terms.
							</p>
							<v-btn
								text
								color="primary"
								small
								@click="clearSearch"
							>
								Clear Search
							</v-btn>
						</v-card>
					</div>          <div
						v-else-if="systems.length === 0"
						class="empty-state"
					>
						<!-- Create System Card (when no systems exist) -->
						<div class="create-system-container">
							<v-card
								class="system-create-card"
								outlined
								:hover="!isLookupDataLoading"
								:class="{ 'disabled-card': isLookupDataLoading }"
								@click="!isLookupDataLoading && handleCreateSystem()"
							>
								<div class="create-card-content">
									<div class="create-icon-container">
										<v-icon
											color="primary"
											size="48"
										>
											mdi-plus-circle
										</v-icon>
									</div>
									<div class="create-content">
										<h3 class="create-title">
											Create New System
										</h3>
										<p class="create-subtitle">
											{{ isLookupDataLoading ? 'Loading available options...' : 'Set up a new system to organize your integrations' }}
										</p>
									</div>
									<div class="create-action">
										<v-btn
											color="primary"
											class="text-none"
											:disabled="isCreatingSystem || isLookupDataLoading"
											:loading="isLookupDataLoading"
											@click="handleCreateSystem"
										>
											{{ isCreatingSystem ? 'Creating...' : 'Get Started' }}
										</v-btn>
									</div>
								</div>
							</v-card>
						</div>
					</div>

					<div v-else>
						<!-- System Cards -->
						<div
							v-for="system in sortedSystems"
							:key="system.systemIntegration.id"
							class="system-item mb-6"
						>
							<SystemCard
								:system-integration="system.systemIntegration"
								:integration-services="system.integrationServices"
								:max-integration-services="maxServicesPerSystem"
								:is-lookup-data-loading="isLookupDataLoading"
								:user-has-log-view-permission="userHasLogViewPermission"
								:user-has-full-connector-service-permission="userHasFullConnectorServicePermission"
								:style="system.systemIntegration.iconColor && system.systemIntegration.iconColor !== 'white' ? { '--icon-color': system.systemIntegration.iconColor } : {}"
								@toggle-system-enabled="$emit('toggle-system-enabled', $event)"
								@toggle-service-enabled="$emit('toggle-service-enabled', $event)"
								@view-service-details="$emit('view-service-details', $event)"
								@edit-service="editService"
								@clone-service="cloneService"
								@view-failures="$emit('view-failures', $event)"
								@delete-service="$emit('delete-service', $event)"
								@edit-system="$emit('edit-system', $event)"
								@export-system="$emit('export-system', $event)"
								@export-service="$emit('export-service', $event)"
								@clone-system="$emit('clone-system', $event)"
								@delete-system="$emit('delete-system', $event)"
								@change:active="$emit('toggle-system-enabled', $event)"
								@create-service="$emit('create-service', $event)"
								@reorder-services="$emit('reorder-services', $event)"
								@upgrade-license="$emit('upgrade-license')"
							/>
						</div>

						<!-- Create System Button -->
						<div
							v-if="showCreateButton"
							class="create-system-container mt-6"
						>
							<v-card
								class="system-create-card"
								outlined
								:hover="!isLookupDataLoading"
								:class="{ 'disabled-card': isLookupDataLoading }"
								@click="!isLookupDataLoading && handleCreateSystem()"
							>
								<div class="create-card-content">
									<div class="create-icon-container">
										<v-icon
											color="primary"
											size="48"
										>
											mdi-plus-circle
										</v-icon>
									</div>
									<div class="create-content">
										<h3 class="create-title">
											Create New System
										</h3>
										<p class="create-subtitle">
											{{ isLookupDataLoading ? 'Loading available options...' : 'Set up a new system to organize your integrations' }}
										</p>
									</div>
									<div class="create-action">
										<v-btn
											color="primary"
											:disabled="isCreatingSystem || isLookupDataLoading"
											:loading="isLookupDataLoading"
											@click="handleCreateSystem"
										>
											{{ isCreatingSystem ? 'Creating...' : 'Get Started' }}
										</v-btn>
									</div>
								</div>
							</v-card>
						</div>
					</div>
				</div>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import SystemCard from './SystemCard.vue'

export default {
	name: 'system-list',
	components: {
		SystemCard

	},
	props: {
		/**
     * Array of system objects, each containing a systemIntegration and its integrationServices
     */
		systems: {
			type: Array,
			required: true,
			default: () => []
		},
		/**
     * Maximum number of services allowed per system
     */
		maxServicesPerSystem: {
			type: Number,
			default: 10
		},
		/**
     * Whether to show create system button
     */
		showCreateButton: {
			type: Boolean,
			default: true
		},
		/**
     * Whether system creation is in progress
     */
		isCreatingSystem: {
			type: Boolean,
			default: false
		},
		/**
     * Whether the parent is still loading lookup data needed by the create flows.
     * Disables every Add System / Add Integration entry point until false.
     */
		isLookupDataLoading: {
			type: Boolean,
			default: false
		},
		/**
     * Optional lookup data for system creation modal
     */
		lookupData: {
			type: Object,
			default: null
		},
		userHasLogViewPermission: {
			type: Boolean,
			default: false
		},
		userHasFullConnectorServicePermission: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			searchQuery: '',
			sortMode: 'newest' // Default to newest sorting
		}
	},
	computed: {
		/**
     * Sorts the filtered systems based on the selected sort mode.
     * @returns {Array} The sorted list of systems.
     */
		sortedSystems () {
			// Create a deep copy to avoid mutating the original array and sort services within each system
			const systems = this.filteredSystems.map(system => ({
				...system,
				integrationServices: [...(system.integrationServices || [])].sort((a, b) => {
					if (this.sortMode === 'newest') {
						// Sort services by creation date (newest first)
						const dateA = Date.parse(a.nonFormattedCreatedDate || 0)
						const dateB = Date.parse(b.nonFormattedCreatedDate || 0)
						return dateB - dateA
					} else {
						// Sort services alphabetically by name (A-Z)
						const nameA = (a.name || '').toLowerCase()
						const nameB = (b.name || '').toLowerCase()
						if (nameA < nameB) return -1
						if (nameA > nameB) return 1
						return 0
					}
				})
			}))

			if (this.sortMode === 'newest') {
				// Sort systems by creation date (newest first)
				return systems.sort((a, b) => {
					const dateA = Date.parse(a.systemIntegration?.nonFormattedCreatedDate || 0)
					const dateB = Date.parse(b.systemIntegration?.nonFormattedCreatedDate || 0)
					return dateB - dateA // Newest first (descending)
				})
			} else {
				// Sort systems alphabetically by name (A-Z)
				return systems.sort((a, b) => {
					const nameA = (a.systemIntegration?.serviceName || '').toLowerCase()
					const nameB = (b.systemIntegration?.serviceName || '').toLowerCase()
					if (nameA < nameB) return -1
					if (nameA > nameB) return 1
					return 0
				})
			}
		},

		/**
     * Filters systems based on search query matching system name or service names.
     * @returns {Array} The filtered list of systems.
     */
		filteredSystems () {
			if (!this.searchQuery) {
				return this.systems
			}
			const lowerCaseQuery = this.searchQuery.toLowerCase()
			return this.systems.filter(system => {
				const systemName = (system.systemIntegration?.serviceName || '').toLowerCase()
				if (systemName.includes(lowerCaseQuery)) {
					return true
				}

				if (system.integrationServices) {
					return system.integrationServices.some(service =>
						(service?.name || '').toLowerCase().includes(lowerCaseQuery)
					)
				}

				return false
			})
		},

		/**
     * Calculates the total number of integrations across all systems.
     * @returns {number} The total count of services.
     */
		totalServices () {
			return this.systems.reduce((total, system) => {
				return total + (system.integrationServices ? system.integrationServices.length : 0)
			}, 0)
		},

		/**
     * Calculates the total number of active (enabled) integrations.
     * @returns {number} The count of active services.
     */
		activeServices () {
			return this.systems.reduce((total, system) => {
				if (!system.systemIntegration.enabled) {
					return total // If the system is disabled, none of its services are active.
				}
				const activeCount = system.integrationServices
					? system.integrationServices.filter(service => service.isEnabled).length
					: 0
				return total + activeCount
			}, 0)
		}
	},
	methods: {
		editService (payload) {
			this.$emit('edit-service', payload)
		},

		cloneService (payload) {
			this.$emit('clone-service', payload)
		},

		/**
     * Clears the search query input.
     */
		clearSearch () {
			this.searchQuery = ''
		},

		/**
     * Shows the system creation modal.
     */
		handleCreateSystem () {
			this.$emit('create-system')
		}

		/**
     * Handles the successful creation of a system.
     * @param {object} systemData - The data of the newly created system.
     */

		/**
     * Handles errors during system creation.
     * @param {Error} error - The error object.
     */

		/**
     * Closes the system creation modal.
     */

	}
}
</script>

<style scoped>
.system-list-container {
  padding: 24px;
}

.system-list {
  padding: 0; /* Remove default v-col padding since container controls it */
}

.search-input {
  background-color: var(--global-card-background, white);
}

.search-input .v-input__control .v-input__slot {
  border-radius: var(--radius);
}

.sort-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sort-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--global-muted-foreground, #616161);
  white-space: nowrap;
}

.sort-toggle {
  flex-shrink: 0;
  background: none !important;
  border: none !important;
  border-radius: var(--radius);
  padding: 2px;
  box-shadow: none !important;
}

.sort-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  min-height: 28px !important;
  width: 32px !important;
  min-width: 32px !important;
  border-radius: var(--radius) !important;
  transition: all 0.2s ease !important;
  color: var(--global-muted-foreground, #616161) !important;
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.sort-btn:not(.v-btn--active) {
  background-color: transparent !important;
}

.sort-btn.v-btn--active {
  background-color: var(--global-muted, #e5e7eb) !important;
  color: inherit !important;
  box-shadow: none !important;
  transform: none !important;
}

.sort-btn:hover:not(.v-btn--active) {
  background-color: transparent !important;
  color: var(--global-foreground, #424242) !important;
}

.sort-btn .v-icon {
  opacity: 0.7;
  margin: 0 !important;
}

.sort-btn.v-btn--active .v-icon {
  opacity: 1;
}

.system-item {
  width: 100%;
  margin-bottom: 2rem; /* Increased spacing between system cards */
}

.system-item:last-child {
  margin-bottom: 0; /* Remove margin from last item */
}

.system-cards-list {
  width: 100%;
}

.empty-state-card {
  margin: 32px auto;
  max-width: 400px;
}

.system-create-card {
  min-height: 120px;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 2px dashed var(--global-border-color, #e0e0e0);
  background-color: var(--global-muted, #fafafa);
}

.system-create-card:hover {
  border-color: var(--border-strong);
  background-color: var(--interactive-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.system-create-card.disabled-card {
  cursor: not-allowed;
  opacity: 0.6;
}

.system-create-card.disabled-card:hover {
  border-color: var(--global-border-color, #e0e0e0);
  background-color: var(--global-muted, #fafafa);
  transform: none;
  box-shadow: none;
}

.create-card-content {
  display: flex;
  align-items: center;
  padding: 24px;
  gap: 20px;
  height: 100%;
}

.create-icon-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--global-muted, rgba(96, 96, 96, 0.1));
}

.create-content {
  flex: 1;
  min-width: 0;
}

.create-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--global-foreground, #212121);
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.create-subtitle {
  font-size: 0.75rem;
  color: var(--global-muted-foreground, #616161);
  margin: 0;
  line-height: 1.4;
}

.create-action {
  flex-shrink: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .search-filter-container {
    max-width: 100%;
    margin-bottom: 20px;
  }

  .search-filter-container .d-flex {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .sort-container {
    align-self: center;
    justify-content: center;
  }

  .sort-label {
    font-size: 13px;
  }

  .system-item {
    margin-bottom: 1.5rem; /* Slightly reduced spacing on mobile */
  }

  .create-card-content {
    flex-direction: column;
    text-align: center;
    padding: 20px 16px;
    gap: 16px;
  }

  .create-icon-container {
    width: 60px;
    height: 60px;
  }

  .create-title {
    font-size: 1rem;
  }

  .create-subtitle {
    font-size: 0.8rem;
  }
}

@media (max-width: 600px) {
  .system-list-container {
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .create-card-content {
    padding: 16px 12px;
  }

  .create-icon-container {
    width: 50px;
    height: 50px;
  }
  .create-icon-container .v-icon {
    font-size: 32px !important;
  }
}

.gap-3 {
  gap: 12px;
}
</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Dark mode overrides for SystemList */
.dark,
.theme--dark {
  .system-list-container .search-input {
    background-color: var(--global-card-background, #1c1c1e) !important;
  }

  .system-list-container .search-input .v-input__slot {
    background-color: var(--global-card-background, #1c1c1e) !important;
  }

  .system-list-container .v-text-field--outlined fieldset {
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .system-list-container .v-text-field input,
  .system-list-container .v-text-field label {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .system-list-container .v-text-field input::placeholder {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-list-container .empty-state-card {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .system-list-container .system-create-card {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .system-list-container .system-create-card:hover {
    background-color: rgba(255, 255, 255, 0.04) !important;
  }
}
</style>
